// HistoryTab — unified activity timeline across Yawmi tabs.
// Aggregates: aweni_yawmi_habit_logs + aweni_yawmi_duas (answered) +
// aweni_yawmi_messages + aweni_yawmi_sport_log into one date-grouped feed.
// BOUNDARY: read-only aggregator. No Islamic content. Hisni activity feed
// is a separate cross-app concern (SDK Activity Feed handles that).
import type { AweniContext } from '../../types';

type ActivityType = 'habit' | 'dua' | 'message' | 'sport';

interface ActivityItem {
  type: ActivityType;
  date: string;            // YYYY-MM-DD
  ts: number;              // ms for sort
  icon: string;
  title: string;
  meta?: string;
}

export class HistoryTab {
  private el: HTMLElement;
  private filter: 'all' | 'today' | 'week' = 'today';

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void { this.render(ctx); }
  unmount(): void { this.el.innerHTML = ''; }

  private collect(ctx: AweniContext): ActivityItem[] {
    const items: ActivityItem[] = [];
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;

    // Habit logs
    try {
      const logs = JSON.parse(localStorage.getItem('aweni_yawmi_habit_logs') || '[]');
      const habits = JSON.parse(localStorage.getItem('aweni_yawmi_habits') || '[]');
      const habitName = (id: string) => {
        const h = habits.find((x: { id: string; name: string }) => x.id === id);
        return h ? (ctx.i18n.t(h.name) || h.name) : id;
      };
      for (const l of logs) {
        if (!l.date) continue;
        items.push({
          type: 'habit', date: l.date,
          ts: new Date(l.date).getTime(),
          icon: '✅',
          title: habitName(l.habitId),
          meta: `${t('yawmi_history_count','count')}: ${l.count || 1}`,
        });
      }
    } catch { /* skip */ }

    // Duas (answered only)
    try {
      const duas = JSON.parse(localStorage.getItem('aweni_yawmi_duas') || '[]');
      for (const d of duas) {
        if (!d.answered || !d.answeredAt) continue;
        items.push({
          type: 'dua', date: d.answeredAt.slice(0, 10),
          ts: new Date(d.answeredAt).getTime(),
          icon: '🤲',
          title: t('yawmi_history_dua_answered','Dua answered'),
          meta: (d.text || '').slice(0, 60),
        });
      }
    } catch { /* skip */ }

    // Messages (PeopleTab)
    try {
      const msgs = JSON.parse(localStorage.getItem('aweni_yawmi_messages') || '[]');
      const contacts = JSON.parse(localStorage.getItem('aweni_yawmi_contacts') || '[]');
      const contactName = (id: string) => {
        const c = contacts.find((x: { id: string; name: string }) => x.id === id);
        return c ? c.name : t('yawmi_history_unknown_contact','Unknown');
      };
      const channelIcon = (ch: string) =>
        ch === 'whatsapp' ? '💬' : ch === 'call' ? '📞' : ch === 'visit' ? '🚪' : '✉️';
      for (const m of msgs) {
        if (!m.sentAt) continue;
        items.push({
          type: 'message', date: m.sentAt.slice(0, 10),
          ts: new Date(m.sentAt).getTime(),
          icon: channelIcon(m.channel),
          title: contactName(m.contactId),
          meta: `${m.channel} · ${m.occasion}`,
        });
      }
    } catch { /* skip */ }

    // Sport log
    try {
      const log = JSON.parse(localStorage.getItem('aweni_yawmi_sport_log') || '[]');
      for (const e of log) {
        if (!e.date) continue;
        items.push({
          type: 'sport', date: e.date,
          ts: new Date(e.date).getTime() + (parseInt(e.id?.slice(2, 6) || '0', 36) % 86400000),
          icon: '🏃',
          title: t('yawmi_history_sport_'+e.sportId, e.sportId),
          meta: `${e.duration} ${t('yawmi_health_min','min')}`,
        });
      }
    } catch { /* skip */ }

    return items.sort((a, b) => b.ts - a.ts);
  }

  private filtered(items: ActivityItem[]): ActivityItem[] {
    if (this.filter === 'all') return items;
    const today = new Date().toISOString().slice(0, 10);
    if (this.filter === 'today') return items.filter(i => i.date === today);
    if (this.filter === 'week') {
      const wk = new Date(); wk.setDate(wk.getDate() - 7);
      const cutoff = wk.toISOString().slice(0, 10);
      return items.filter(i => i.date >= cutoff);
    }
    return items;
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    const locale = ctx.i18n.locale || 'en';
    const all = this.collect(ctx);
    const items = this.filtered(all);

    // Group by date
    const grouped: Record<string, ActivityItem[]> = {};
    for (const it of items) (grouped[it.date] ??= []).push(it);
    const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    const formatDate = (iso: string) => {
      try {
        return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(iso));
      } catch { return iso; }
    };

    this.el.innerHTML = `
      <style>
        .ht2-filters{display:flex;gap:6px;margin-bottom:12px}
        .ht2-filter{flex:1;padding:6px 12px;border:1.5px solid var(--ln-border);
          border-radius:var(--ln-radius-full,9999px);background:var(--ln-bg-card,#fff);
          color:var(--ln-text-secondary);font-size:.8rem;font-weight:700;cursor:pointer}
        .ht2-filter--active{background:var(--ln-accent);color:var(--ln-accent-text);border-color:var(--ln-accent)}
        .ht2-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:12px}
        .ht2-stat{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.08));
          border-radius:var(--ln-radius-md,10px);padding:6px;text-align:center}
        .ht2-stat-n{font-size:1.1rem;font-weight:800}
        .ht2-stat-l{font-size:.62rem;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.4px}
        .ht2-group{margin-bottom:14px}
        .ht2-day{font-size:.7rem;font-weight:700;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.5px;
          margin-bottom:6px;padding:0 2px;position:sticky;top:0;background:var(--ln-bg-primary,#FAF7F2);padding:4px 0;z-index:1}
        .ht2-list{display:flex;flex-direction:column;gap:6px}
        .ht2-item{display:flex;align-items:center;gap:10px;padding:8px 10px;background:var(--ln-bg-card,#fff);
          border:1px solid var(--ln-border,rgba(0,0,0,.06));border-radius:var(--ln-radius-md,10px)}
        .ht2-ic{font-size:1.1rem;flex-shrink:0}
        .ht2-info{flex:1;min-width:0}
        .ht2-title{font-size:.88rem;font-weight:600;color:var(--ln-text-primary,#222);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .ht2-meta{font-size:.7rem;color:var(--ln-text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .ht2-tag{font-size:.6rem;padding:2px 6px;border-radius:var(--ln-radius-sm,4px);text-transform:uppercase;letter-spacing:.4px}
        .ht2-tag--habit{background:rgba(34,197,94,.12);color:#16a34a}
        .ht2-tag--dua{background:rgba(168,85,247,.12);color:#9333ea}
        .ht2-tag--message{background:rgba(59,130,246,.12);color:#2563eb}
        .ht2-tag--sport{background:rgba(245,158,11,.12);color:#d97706}
        .ht2-empty{text-align:center;padding:32px;color:var(--ln-text-secondary)}
      </style>

      <div class="ht2-filters">
        ${(['today','week','all'] as const).map(f => `
          <button class="ht2-filter${f===this.filter?' ht2-filter--active':''}" data-filter="${f}">
            ${escHtml(t('yawmi_history_filter_'+f, f))}
          </button>`).join('')}
      </div>

      <div class="ht2-stats">
        <div class="ht2-stat"><div class="ht2-stat-n">${items.filter(i=>i.type==='habit').length}</div><div class="ht2-stat-l">✅</div></div>
        <div class="ht2-stat"><div class="ht2-stat-n">${items.filter(i=>i.type==='dua').length}</div><div class="ht2-stat-l">🤲</div></div>
        <div class="ht2-stat"><div class="ht2-stat-n">${items.filter(i=>i.type==='message').length}</div><div class="ht2-stat-l">💬</div></div>
        <div class="ht2-stat"><div class="ht2-stat-n">${items.filter(i=>i.type==='sport').length}</div><div class="ht2-stat-l">🏃</div></div>
      </div>

      ${dates.length === 0
        ? `<div class="ht2-empty">${escHtml(t('yawmi_history_empty','No activity in this period.'))}</div>`
        : dates.map(d => `
            <div class="ht2-group">
              <div class="ht2-day">${escHtml(formatDate(d))}</div>
              <div class="ht2-list">
                ${grouped[d].map(it => `
                  <div class="ht2-item" role="listitem">
                    <span class="ht2-ic" aria-hidden="true">${it.icon}</span>
                    <div class="ht2-info">
                      <div class="ht2-title">${escHtml(it.title)}</div>
                      ${it.meta ? `<div class="ht2-meta">${escHtml(it.meta)}</div>` : ''}
                    </div>
                    <span class="ht2-tag ht2-tag--${it.type}">${escHtml(it.type)}</span>
                  </div>`).join('')}
              </div>
            </div>`).join('')}
    `;

    this.el.querySelectorAll<HTMLButtonElement>('.ht2-filter').forEach(b => {
      b.addEventListener('click', () => {
        this.filter = b.dataset.filter as typeof this.filter;
        this.render(ctx);
      });
    });
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
